import { PersonSmartSearchResult } from '@ltvco/refresh-lib/api';
import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { useLLMSmartSearchV1 } from '@ltvco/refresh-lib/hooks';
import { Box, Grid, Link } from '@ltvco/refresh-lib/theme';
import { searchFormsRecyclingValidation } from '@ltvco/refresh-lib/utils';
import { useSession, useReportRedirect } from '@ltvco/refresh-lib/v1';
import {
  AdvancedSearchPersonModal,
  personFormsCollectionTypesRecords,
  PersonSearchHintsDropdown,
  SearchPersonAdvancedModal,
} from '@ltvco/refresh-lib/v2';
import {
  useFeatureIsOn,
  useForm,
  yupResolver,
} from '@ltvco/refresh-lib/vendors';
import React, {
  useContext,
  useCallback,
  useEffect,
  useState,
  useRef,
} from 'react';

import { SearchButton } from '../components';
import { SearchProps } from '../utils';

import { PersonFormContainer, PersonSearchFieldContainer } from './components';
import { PersonSmartSearchInput } from './PersonSmartSearchInput';
import {
  PERSON_SEARCH_NO_RESULTS_ERROR_MESSAGE,
  schemaPersonSearchForm,
} from './utils';
type SmartSearchPersonParams = {
  fn?: string;
  ln?: string;
  city?: string;
  email?: string;
  mn?: string;
  phone?: string;
  state?: string;
  street?: string;
  type?: string;
  unit?: string;
  zipcode?: string;
  username?: string;
};

export interface PersonFormData {
  name: string;
}

export const PersonSearch: React.FC<SearchProps> = ({
  isMobile,
  placeholderText,
  placement = 'dashboard',
}) => {
  const {
    session: { account },
  } = useSession();
  const { trackEventGA4 } = useContext(AppConfig);
  const { redirectToSearchPersonUrlv2 } = useReportRedirect();
  const [isInputFocused, setIsInputFocused] = useState(false);
  const [currentInputValue, setCurrentInputValue] = useState('');
  const [noResults, setNoResults] = useState(false);
  const isOnPersonSearchHintsVariation = useFeatureIsOn('prop-3159');
  const userInExactAgeInputVariation = useFeatureIsOn('RTRN-1090');
  const isInV2EndpointVariation = useFeatureIsOn('RFRSH-4022');
  const isInSearchResultsVariation = useFeatureIsOn('RFRSH-4236');
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    setError,
    clearErrors,
    formState: { errors },
    control,
  } = useForm<PersonFormData>({
    resolver: yupResolver(schemaPersonSearchForm),
    defaultValues: {
      name: '',
    },
  });

  const containerRef = useRef<HTMLDivElement>(null);

  const name = watch('name');

  const handleClearName = useCallback(() => {
    setValue('name', '');
    clearErrors('name');
  }, [setValue, clearErrors]);

  const redirectToConsolidatedPerson = (response: {
    results?: Partial<PersonSmartSearchResult>[];
    data?: { result?: Partial<PersonSmartSearchResult> };
  }) => {
    const results =
      Array.isArray(response?.results) && response.results.length
        ? response.results
        : response?.data?.result
        ? [response.data.result]
        : [];

    const finalData = results.reduce(
      (
        acc: Record<string, string | string[]>,
        curr: SmartSearchPersonParams
      ) => {
        for (const key in curr) {
          const currentValue = curr[key as keyof SmartSearchPersonParams];
          if (currentValue) {
            acc[key] = currentValue;
          }
        }
        return acc;
      },
      {}
    );

    const { street, city, state, fn, mn, ln, title, company, age } = finalData;
    if (ln) {
      const personInfo = {
        age,
        city,
        state,
        fName: fn,
        lName: ln,
        mname: mn,
        fullName: fn && ln ? `${fn} ${ln}` : '',
        company,
        title,
        address: street,
      };

      setNoResults(false);

      if (userInExactAgeInputVariation && personInfo.age) {
        const modifiedAge = (Number(personInfo.age) + 2).toString();
        const ageSource = 'input';

        redirectToSearchPersonUrlv2({
          ...personInfo,
          age: modifiedAge,
          ageSource,
          mName: mn,
          advancedSearch: true,
          source: Object.keys(personFormsCollectionTypesRecords),
        });

        return;
      }

      redirectToSearchPersonUrlv2({
        ...personInfo,
        mName: mn,
        advancedSearch: true,
        source: Object.keys(personFormsCollectionTypesRecords),
      });

      return;
    }

    setNoResults(true);
  };

  const { data } = useLLMSmartSearchV1('personSmartSearchV1', {
    enabled: Boolean(currentInputValue),
    searchValue: currentInputValue,
    useV2Endpoint: isInV2EndpointVariation,
  });

  useEffect(() => {
    if (data?.results?.length || data?.data?.result) {
      redirectToConsolidatedPerson(data);
    } else if (data?.contextId) {
      setNoResults(true);
    }
  }, [data?.results, data?.contextId, data?.data?.result]);

  useEffect(() => {
    if (noResults && name?.trim()) {
      setError('name', { message: PERSON_SEARCH_NO_RESULTS_ERROR_MESSAGE });
    }
  }, [noResults, name, setError]);

  const [advancedSearchModalOpen, setAdvancedSearchModalOpen] = useState(false);

  const closeModal = () => {
    setAdvancedSearchModalOpen(false);
  };

  const onFocus = () => {
    setIsInputFocused(true);
  };

  const onBlur = () => {
    setIsInputFocused(false);
  };

  const onPersonSearchSubmit = useCallback(
    ({ name }: PersonFormData) => {
      if (!searchFormsRecyclingValidation(account)) {
        return;
      }

      // if value is present but didn't change, set or restore error and return
      if (name && name === currentInputValue) {
        setNoResults(true);
        setError('name', {
          message: PERSON_SEARCH_NO_RESULTS_ERROR_MESSAGE,
        });

        return;
      }

      setNoResults(false);
      clearErrors('name');

      trackEventGA4({
        event_name: 'report_search',
        report_type: 'people',
        search_by: 'name',
        search_from: `${placement}_search`,
      });

      setCurrentInputValue(name);
    },
    [
      account,
      currentInputValue,
      name,
      setCurrentInputValue,
      setNoResults,
      clearErrors,
      trackEventGA4,
    ]
  );

  const renderMobileForm = useCallback(() => {
    return (
      <Grid container sx={{ mt: 2.5, mb: 2.5 }} gap={3}>
        <PersonSearchFieldContainer
          ref={containerRef}
          className={errors.name ? 'error' : ''}
        >
          <PersonSmartSearchInput
            register={register}
            placeholder={placeholderText}
            errors={errors}
            clearErrors={(field?: string) => clearErrors(field as 'name')}
            onClear={handleClearName}
            value={name}
            control={control}
            onFocus={onFocus}
            onBlur={onBlur}
          />
          {isOnPersonSearchHintsVariation && isInputFocused && (
            <PersonSearchHintsDropdown
              value={name}
              setAdvancedSearchModalVisible={setAdvancedSearchModalOpen}
              isInputFocused={isInputFocused}
              sx={{ top: -20, mt: 0 }}
            />
          )}
        </PersonSearchFieldContainer>
        <SearchButton type="submit" fullWidth sx={{ order: 0 }}>
          Search
        </SearchButton>
        <Grid container justifyContent="flex-end" width="100%">
          <Link
            variant="h6"
            color="text.secondary"
            width="125px"
            onClick={() => {
              trackEventGA4({
                event_name: 'advanced_search_click',
                action: 'expand',
              });
              setAdvancedSearchModalOpen(true);
            }}
            sx={{
              cursor: 'pointer',
            }}
          >
            Advanced Search
          </Link>
        </Grid>
      </Grid>
    );
  }, [register, placeholderText, name, errors, clearErrors, handleClearName]);

  const renderDesktopForm = useCallback(() => {
    return (
      <PersonFormContainer>
        <Box flexGrow={1}>
          <PersonSearchFieldContainer
            container
            className={errors.name ? 'error' : ''}
          >
            <Grid
              size={{ xs: 12 }}
              sx={{ display: 'flex', alignItems: 'center' }}
              ref={containerRef}
            >
              <PersonSmartSearchInput
                register={register}
                placeholder={placeholderText}
                errors={errors}
                clearErrors={(field?: string) => clearErrors(field as 'name')}
                onClear={handleClearName}
                value={name}
                control={control}
                onFocus={onFocus}
                onBlur={onBlur}
              />
            </Grid>
            {isOnPersonSearchHintsVariation && isInputFocused && (
              <PersonSearchHintsDropdown
                value={name}
                setAdvancedSearchModalVisible={setAdvancedSearchModalOpen}
                isInputFocused={isInputFocused}
              />
            )}
          </PersonSearchFieldContainer>
        </Box>
        <SearchButton type="submit">Search</SearchButton>
        <Link
          variant="h6"
          color="text.secondary"
          onClick={() => {
            trackEventGA4({
              event_name: 'advanced_search_click',
              action: 'expand',
            });
            setAdvancedSearchModalOpen(true);
          }}
          sx={(theme) => ({
            cursor: 'pointer',
            py: 0,
            px: theme.spacing(1),
            marginBottom: theme.spacing(0.5),
            position: 'absolute',
            right: 0,
            bottom: 0,
          })}
        >
          Advanced Search
        </Link>
      </PersonFormContainer>
    );
  }, [register, placeholderText, name, errors, clearErrors, handleClearName]);

  return (
    <form onSubmit={handleSubmit(onPersonSearchSubmit)} autoComplete="off">
      {isMobile ? renderMobileForm() : renderDesktopForm()}

      {isInSearchResultsVariation ? (
        <AdvancedSearchPersonModal
          open={advancedSearchModalOpen}
          closeModal={closeModal}
          userInExactAgeInputVariation={userInExactAgeInputVariation}
        />
      ) : (
        <SearchPersonAdvancedModal
          open={advancedSearchModalOpen}
          closeModal={closeModal}
          userInExactAgeInputVariation={userInExactAgeInputVariation}
        />
      )}
    </form>
  );
};
